// ─────────────────────────────────────────────────────────────────────────────
// Sparkline
//
// A compact SVG line chart for detector metric history. ~150px wide, no axes,
// no legend. Just the trend.
//
// Props:
//   points          [{ date: 'YYYY-MM-DD', value: number }, ...]  asc by date
//   width           default 150
//   height          default 40
//   direction       'down' | 'up'  — used to color-code "good" vs "bad" trends
//   target          number | null  — optional horizontal target line
//   formatter       (v) => string  — for tooltip values
//   ariaLabel       accessibility label
//
// Color logic:
//   The line color follows the trend. We compare the FIRST half of the series
//   to the SECOND half: if the second half is "better" than the first (per
//   direction), green; if worse, red; if roughly equal, blue. This works for
//   small samples where any one point can be noisy.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { C } from "../lib/colors";

export function Sparkline({
  points = [],
  width = 150,
  height = 40,
  direction = "down",
  target = null,
  formatter = (v) => Number(v).toFixed(2),
  ariaLabel = "Metric trend",
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const pad = { top: 4, right: 4, bottom: 4, left: 4 };

  if (!points || points.length < 2) {
    return (
      <div
        style={{
          width, height,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, color: C.muted, fontStyle: "italic",
          border: `1px dashed ${C.border}`, borderRadius: 4,
        }}
        title={!points || points.length === 0 ? "No history yet" : "Need ≥2 days of history"}
      >
        {points.length === 0 ? "no history" : "≥2 days needed"}
      </div>
    );
  }

  // Compute trend color
  const values = points.map(p => Number(p.value));
  const mid = Math.floor(values.length / 2);
  const firstAvg = values.slice(0, mid).reduce((s, v) => s + v, 0) / Math.max(1, mid);
  const secondAvg = values.slice(mid).reduce((s, v) => s + v, 0) / Math.max(1, values.length - mid);
  let trendColor = C.blue;
  if (firstAvg !== secondAvg) {
    const movedDown = secondAvg < firstAvg;
    const isGood = (direction === "down") ? movedDown : !movedDown;
    // Only color strongly if change is >5% of the range
    const range = Math.max(...values) - Math.min(...values);
    const delta = Math.abs(secondAvg - firstAvg);
    if (range > 0 && delta / range > 0.05) {
      trendColor = isGood ? C.green : C.red;
    }
  }

  // Scaling: include target in the y-range so it's always visible
  const yValues = target != null ? [...values, Number(target)] : values;
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const ySpan = yMax - yMin || 1; // guard div-by-zero on flat series

  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const xAt = (i) => pad.left + (i / (points.length - 1)) * innerW;
  const yAt = (v) => pad.top + (1 - (v - yMin) / ySpan) * innerH;

  const pointsAttr = points.map((p, i) => `${xAt(i)},${yAt(Number(p.value))}`).join(" ");
  const targetY = target != null ? yAt(Number(target)) : null;

  return (
    <div style={{ position: "relative", width, height }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-label={ariaLabel}
        role="img"
        style={{ display: "block" }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Target reference line */}
        {targetY != null && (
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={targetY}
            y2={targetY}
            stroke={C.green}
            strokeWidth="1"
            strokeDasharray="2 2"
            opacity="0.6"
          />
        )}

        {/* The line itself */}
        <polyline
          points={pointsAttr}
          fill="none"
          stroke={trendColor}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Dots — slightly larger on the most recent point */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={xAt(i)}
            cy={yAt(Number(p.value))}
            r={i === points.length - 1 ? 2.5 : 1.5}
            fill={trendColor}
          />
        ))}

        {/* Invisible hover targets — wide vertical strips for easier hover */}
        {points.map((p, i) => {
          const stripW = innerW / points.length;
          return (
            <rect
              key={`hover-${i}`}
              x={xAt(i) - stripW / 2}
              y={0}
              width={stripW}
              height={height}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              style={{ cursor: "default" }}
            />
          );
        })}

        {/* Hover indicator dot */}
        {hoverIdx != null && (
          <circle
            cx={xAt(hoverIdx)}
            cy={yAt(Number(points[hoverIdx].value))}
            r="3"
            fill={trendColor}
            stroke={C.text}
            strokeWidth="0.5"
          />
        )}
      </svg>

      {/* Tooltip */}
      {hoverIdx != null && (
        <div
          style={{
            position: "absolute",
            top: -28,
            left: Math.max(0, Math.min(width - 90, xAt(hoverIdx) - 45)),
            background: "#0b0d19",
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            padding: "3px 6px",
            fontSize: 10,
            color: C.text,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            fontFamily: "monospace",
            zIndex: 10,
          }}
        >
          <span style={{ color: C.muted }}>{points[hoverIdx].date.slice(5)}</span>
          {" · "}
          <span>{formatter(points[hoverIdx].value)}</span>
        </div>
      )}
    </div>
  );
}
